import axios from 'axios';

import { logApiError, extractApiError, ApiErrorDetails } from '../utils/errorUtils';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
// Stripe price ids per plan, supplied by the API so test -> live is a server-side swap
export interface StripePrices {
  basic?: string;
  plus?: string;
  premium?: string;
  enterprise?: string;
}

// Stripe configuration. `mode` and `prices` are optional for backward
// compatibility with API versions that only return the publishable key.
export interface StripeConfig {
  publishableKey: string;
  mode?: 'test' | 'live';
  prices?: StripePrices;
}

// Checkout session request
export interface CreateCheckoutSessionRequest {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
}

// Checkout session response
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// Portal session response
export interface PortalSessionResponse {
  url: string;
}

// Subscription details
export interface Subscription {
  practiceId: string;
  status: string;
  tier: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  isTrial: boolean;
  trialEndDate?: string;
}

// Invoice details
export interface Invoice {
  id: string;
  invoiceNumber?: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: string;
  dueDate?: string;
  hostedInvoiceUrl?: string;
  invoicePdfUrl?: string;
  description?: string;
}

// Invoice list response
export interface InvoiceList {
  invoices: Invoice[];
  hasMore: boolean;
}

// Invoice list result with error state (HA-228)
export interface InvoiceListResult {
  data: InvoiceList | null;
  error: ApiErrorDetails | null;
}

// Create axios instance for billing API
const billingApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/billing`,
  withCredentials: true,
});

// Add authorization header to all requests
installSessionInterceptors(billingApi);

/**
 * Get Stripe config (publishable key, mode and plan price ids)
 */
export const getStripeConfig = async (): Promise<StripeConfig | null> => {
  try {
    const response = await billingApi.get('/config');

    return response.data;
  } catch (error) {
    logApiError('getStripeConfig', error);

    return null;
  }
};

/**
 * Create a Stripe checkout session for subscription purchase
 */
export const createCheckoutSession = async (
  request: CreateCheckoutSessionRequest
): Promise<CheckoutSessionResponse | null> => {
  try {
    const response = await billingApi.post('/create-checkout-session', request);

    return response.data;
  } catch (error) {
    logApiError('createCheckoutSession', error);

    return null;
  }
};

/**
 * Create a Stripe customer portal session for managing subscription
 */
export const createPortalSession = async (
  returnUrl: string
): Promise<PortalSessionResponse | null> => {
  try {
    const response = await billingApi.post(
      `/create-portal-session?returnUrl=${encodeURIComponent(returnUrl)}`
    );

    return response.data;
  } catch (error) {
    logApiError('createPortalSession', error);

    return null;
  }
};

/**
 * Get current subscription details
 */
export const getSubscription = async (): Promise<Subscription | null> => {
  try {
    const response = await billingApi.get('/subscription');

    return response.data;
  } catch (error) {
    logApiError('getSubscription', error);

    return null;
  }
};

/**
 * Cancel subscription at period end
 */
export const cancelSubscription = async (): Promise<boolean> => {
  try {
    await billingApi.post('/cancel-subscription');

    return true;
  } catch (error) {
    logApiError('cancelSubscription', error);

    return false;
  }
};

/**
 * Get billing invoices
 * Returns data and error separately so UI can distinguish between:
 * - API error (show retry button)
 * - Empty invoices (show appropriate message based on subscription status)
 */
export const getInvoices = async (limit: number = 10): Promise<InvoiceListResult> => {
  try {
    const response = await billingApi.get(`/invoices?limit=${limit}`);

    return { data: response.data, error: null };
  } catch (error) {
    logApiError('getInvoices', error);

    return { data: null, error: extractApiError(error) };
  }
};

/**
 * Redirect to Stripe checkout for subscription purchase
 */
export const redirectToCheckout = async (priceId: string): Promise<void> => {
  const successUrl = `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${window.location.origin}/subscription`;

  const session = await createCheckoutSession({
    priceId,
    successUrl,
    cancelUrl,
  });

  if (session?.url) {
    window.location.href = session.url;
  } else {
    throw new Error('Failed to create checkout session');
  }
};

/**
 * Verify a completed checkout session and update subscription status
 */
export const verifyCheckoutSession = async (sessionId: string): Promise<Subscription | null> => {
  try {
    const response = await billingApi.post(`/verify-session/${sessionId}`);

    return response.data;
  } catch (error) {
    logApiError('verifyCheckoutSession', error);

    return null;
  }
};

/**
 * Redirect to Stripe customer portal for managing subscription
 */
export const redirectToPortal = async (): Promise<void> => {
  const returnUrl = `${window.location.origin}/subscription`;

  const session = await createPortalSession(returnUrl);

  if (session?.url) {
    window.location.href = session.url;
  } else {
    throw new Error('Failed to create portal session');
  }
};

/**
 * Format currency amount from cents to display string
 */
export const formatCurrency = (amountInCents: number, currency: string = 'usd'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountInCents / 100);
};

/**
 * Get human-readable subscription status
 */
export const getStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    active: 'Active',
    past_due: 'Past Due',
    canceled: 'Canceled',
    trialing: 'Trial',
    incomplete: 'Incomplete',
    incomplete_expired: 'Expired',
    unpaid: 'Unpaid',
    none: 'No Subscription',
  };

  return statusLabels[status] || status;
};

/**
 * Get status color for UI display
 */
export const getStatusColor = (
  status: string
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    active: 'success',
    past_due: 'warning',
    canceled: 'error',
    trialing: 'info',
    incomplete: 'warning',
    incomplete_expired: 'error',
    unpaid: 'error',
    none: 'default',
  };

  return statusColors[status] || 'default';
};
